
/* FRAME Universal — AJAX cart progressive enhancement.
   Prevents duplicate drawer animations during cart refresh.
   Native Shopify cart forms remain available as fallback. */

(() => {
  'use strict';

  const drawer = document.querySelector('[data-frame-cart-drawer]');
  if (!drawer) return;

  const routes = {
    cart: drawer.dataset.cartUrl,
    add: drawer.dataset.cartAddUrl,
    change: drawer.dataset.cartChangeUrl,
    update: drawer.dataset.cartUpdateUrl,
    root: drawer.dataset.cartRootUrl
  };

  let busy = false;
  let previousFocus = null;

  const $ = (selector, context = drawer) =>
    context.querySelector(selector);

  const $$ = (selector, context = drawer) =>
    [...context.querySelectorAll(selector)];

  function showError(message) {
    const alert = $('[data-cart-error]');
    if (!alert) return;
    alert.textContent =
      message || 'Something went wrong. Please try again.';
    alert.hidden = false;
  }

  function clearError() {
    const alert = $('[data-cart-error]');
    if (alert) {
      alert.textContent = '';
      alert.hidden = true;
    }
  }

  function setBusy(value) {
    busy = value;
    const panel = $('.frame-cart__panel');

    if (panel) {
      panel.setAttribute('aria-busy', String(value));
    }

    $$(
      '[data-cart-qty], [data-cart-remove], ' +
      '[data-cart-input], [data-cart-save-note], ' +
      '.frame-cart__quick-add'
    ).forEach(control => {
      if (
        control instanceof HTMLButtonElement ||
        control instanceof HTMLInputElement
      ) {
        control.disabled = value;
      }
    });
  }

  function visible() {
    return !drawer.hidden;
  }

  function open(trigger) {
    if (!visible()) {
      previousFocus = trigger || document.activeElement;

      drawer.hidden = false;
      document.body.classList.add('frame-cart-open');
      drawer.classList.add('is-open');

      $('[data-cart-close].frame-cart__close')
        ?.focus({ preventScroll: true });
    }
  }

  function close() {
    if (!visible()) return;

    drawer.hidden = true;
    drawer.classList.remove('is-open');
    document.body.classList.remove('frame-cart-open');

    if (
      previousFocus &&
      previousFocus.isConnected &&
      typeof previousFocus.focus === 'function'
    ) {
      previousFocus.focus({ preventScroll: true });
    }

    previousFocus = null;
  }

  function cartCount(cart) {
    if (!cart) return;

    const count = Number(cart.item_count) || 0;

    document.querySelectorAll('[data-frame-cart-count]')
      .forEach(el => {
        el.textContent = String(count);
      });

    document.querySelectorAll('a.header-cart')
      .forEach(el => {
        el.setAttribute(
          'aria-label',
          `Cart, ${count} ${count === 1 ? 'item' : 'items'}`
        );
      });

    document.dispatchEvent(
      new CustomEvent('frame:cart-updated', {
        detail: { cart }
      })
    );
  }

  async function request(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        ...options.headers
      }
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      const raw =
        (data && typeof data.description === 'string' &&
          data.description) ||
        (data && typeof data.message === 'string' &&
          data.message) ||
        'Unable to update your cart.';

      throw new Error(raw);
    }

    return data;
  }

  async function getRenderedDrawer() {
    const separator = routes.root.includes('?') ? '&' : '?';

    const response = await fetch(
      `${routes.root}${separator}sections=cart-drawer`,
      {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        'Unable to refresh your cart. Please visit the cart page.'
      );
    }

    const data = await response.json();

    if (
      !data ||
      typeof data['cart-drawer'] !== 'string' ||
      !data['cart-drawer'].trim()
    ) {
      throw new Error('Unable to refresh your cart.');
    }

    const doc = new DOMParser().parseFromString(
      data['cart-drawer'],
      'text/html'
    );

    const fresh = doc.querySelector('#FrameCartDrawer');

    if (!fresh) {
      throw new Error('Unable to refresh your cart.');
    }

    return fresh;
  }

  async function refresh() {
    const [cart, fresh] = await Promise.all([
      request(`${routes.cart}.js`),
      getRenderedDrawer()
    ]);

    const focused = document.activeElement;
    const focusWasInDrawer =
      visible() && drawer.contains(focused);

    const row = focused?.closest?.('[data-cart-line]');
    const line = row?.dataset.cartLine;

    const focusSelector =
      focused?.matches?.('[data-cart-input]')
        ? '[data-cart-input]'
        : focused?.matches?.('[data-cart-qty]')
          ? `[data-cart-qty="${focused.dataset.cartQty}"]`
          : null;

    const oldScroll = $('.frame-cart__scroll');
    const scrollPosition = oldScroll?.scrollTop || 0;
    const noteWasOpen = $('.frame-cart__note')?.open || false;

    /*
      STUTTER FIX

      Previously:
        drawer.innerHTML = fresh.innerHTML;

      This recreated the animated overlay and sliding panel
      every time the cart refreshed.

      The result was a second slide/fade animation.

      Instead, preserve both elements and update only the
      panel's inner content when it has actually changed.
    */

    const panel = $('.frame-cart__panel');
    const freshPanel = fresh.querySelector('.frame-cart__panel');

    if (!panel || !freshPanel) {
      throw new Error('Unable to update the cart drawer.');
    }

    const changed = panel.innerHTML !== freshPanel.innerHTML;

    if (changed) {
      panel.innerHTML = freshPanel.innerHTML;

      const newScroll = $('.frame-cart__scroll');

      if (newScroll) {
        newScroll.scrollTop = scrollPosition;
      }

      const newNote = $('.frame-cart__note');

      if (newNote && noteWasOpen) {
        newNote.open = true;
      }
    }

    if (focusWasInDrawer && changed) {
      const restored =
        line && focusSelector
          ? $(`[data-cart-line="${line}"] ${focusSelector}`)
          : null;

      (
        restored ||
        $('[data-cart-close].frame-cart__close')
      )?.focus({ preventScroll: true });
    }

    cartCount(cart);
    clearError();

    return cart;
  }

  async function mutate(action) {
    if (busy) return;

    setBusy(true);
    clearError();

    let changed = false;

    try {
      await action();
      changed = true;
      await refresh();
    } catch (error) {
      showError(
        error.message || 'Unable to update your cart.'
      );

      if (changed) {
        try {
          cartCount(
            await request(`${routes.cart}.js`)
          );
        } catch (_error) {}
      }
    } finally {
      setBusy(false);
    }
  }

  async function changeQuantity(line, quantity) {
    if (
      !Number.isSafeInteger(line) ||
      line < 1 ||
      !Number.isSafeInteger(quantity) ||
      quantity < 0 ||
      quantity > 999
    ) {
      showError('Enter a whole-number quantity from 0 to 999.');
      return;
    }

    await mutate(() =>
      request(`${routes.change}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          line,
          quantity
        })
      })
    );
  }

  drawer.addEventListener('click', event => {
    const closeButton =
      event.target.closest('[data-cart-close]');

    if (closeButton) {
      event.preventDefault();
      close();
      return;
    }

    const row = event.target.closest('[data-cart-line]');

    if (!row || busy) return;

    const line = Number(row.dataset.cartLine);
    const qtyButton =
      event.target.closest('[data-cart-qty]');

    if (qtyButton) {
      event.preventDefault();

      const input = $('[data-cart-input]', row);

      const next = Math.max(
        0,
        Number(input?.value || 0) +
          Number(qtyButton.dataset.cartQty)
      );

      changeQuantity(line, next);
      return;
    }

    if (event.target.closest('[data-cart-remove]')) {
      event.preventDefault();
      changeQuantity(line, 0);
    }
  });

  drawer.addEventListener('change', event => {
    const input =
      event.target.closest('[data-cart-input]');

    if (!input || busy) return;

    const line = Number(
      input.closest('[data-cart-line]')?.dataset.cartLine
    );

    const raw = input.value.trim();
    const quantity = Number(raw);

    if (
      raw === '' ||
      !Number.isSafeInteger(quantity) ||
      quantity < 0 ||
      quantity > 999
    ) {
      showError('Enter a whole-number quantity from 0 to 999.');
      return;
    }

    changeQuantity(line, quantity);
  });

  drawer.addEventListener('click', event => {
    const button =
      event.target.closest('[data-cart-save-note]');

    if (!button || busy) return;

    const note = $('[data-cart-note]')?.value || '';
    const status = $('[data-cart-note-status]');

    if (status) {
      status.textContent = 'Saving…';
    }

    mutate(() =>
      request(`${routes.update}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ note })
      })
    ).then(() => {
      const refreshed = $('[data-cart-note-status]');

      if (refreshed) {
        refreshed.textContent = 'Instructions saved.';
      }
    });
  });

  document.addEventListener('click', event => {
    const trigger =
      event.target.closest('[data-frame-cart-open]');

    if (
      !trigger ||
      event.defaultPrevented ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return;
    }

    event.preventDefault();
    open(trigger);

    if (!busy) {
      mutate(async () => {});
    }
  });

  document.addEventListener('submit', event => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) return;

    if (!form.matches('form[action*="/cart/add"]')) return;

    if (
      !form.closest(
        '[data-frame-product],[data-frame-quick-add]'
      )
    ) {
      return;
    }

    const submitter = event.submitter;

    if (
      submitter &&
      submitter.name &&
      submitter.name !== 'add'
    ) {
      return;
    }

    if (event.defaultPrevented || busy) return;

    const variantId =
      form.querySelector('[name="id"]')?.value;

    if (!variantId) return;

    event.preventDefault();

    const payload = new FormData(form);

    payload.delete('form_type');
    payload.delete('utf8');
    payload.delete('product-id');
    payload.delete('section-id');

    const validQty = Number(
      payload.get('quantity') || 1
    );

    if (
      !Number.isSafeInteger(validQty) ||
      validQty < 1
    ) {
      showError('Choose a valid quantity.');
      open(submitter || form);
      return;
    }

    open(submitter || form);

    mutate(() =>
      request(`${routes.add}.js`, {
        method: 'POST',
        body: payload
      })
    );
  });

  document.addEventListener('keydown', event => {
    if (!visible()) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusables = $$(
      'a[href],button:not([disabled]),' +
      'input:not([disabled]),textarea:not([disabled]),' +
      'select:not([disabled]),summary,' +
      '[tabindex]:not([tabindex="-1"])'
    ).filter(
      el =>
        el.getAttribute('tabindex') !== '-1' &&
        !el.closest('[hidden]') &&
        el.getClientRects().length
    );

    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (
      event.shiftKey &&
      document.activeElement === first
    ) {
      event.preventDefault();
      last.focus();
    } else if (
      !event.shiftKey &&
      document.activeElement === last
    ) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener(
    'shopify:section:unload',
    event => {
      if (event.target.contains(drawer)) {
        document.body.classList.remove('frame-cart-open');
      }
    }
  );
})();
